import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, setQueryClient } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ToastProvider, useToast } from './components/common/Toast';
import { setToastFunction } from './config/axios';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/layout/Layout';

// Pages
import Login from './pages/shared/Login';
import Register from './pages/shared/Register';
import VerifyEmail from './pages/shared/VerifyEmail';
import ForgotPassword from './pages/shared/ForgotPassword';
import ResetPassword from './pages/shared/ResetPassword';
import Home from './pages/shared/Home';
import Dashboard from './pages/shared/Dashboard';
import AdminDashboard from './pages/admin/Dashboard';
import TeacherApproval from './pages/admin/TeacherApproval';
import UsersManagement from './pages/admin/UsersManagement';
import ContentManagement from './pages/admin/ContentManagement';
import SubscriptionsManagement from './pages/admin/SubscriptionsManagement';
import TeacherDashboard from './pages/teacher/Dashboard';
import CreateCourse from './pages/teacher/CreateCourse';
import CreateLesson from './pages/teacher/CreateLesson';
import CreateQuiz from './pages/teacher/CreateQuiz';
import QuizzesManagement from './pages/teacher/QuizzesManagement';
import EditQuiz from './pages/teacher/EditQuiz';
import QuizResults from './pages/teacher/QuizResults';
import TeacherCourses from './pages/teacher/Courses';
import TeacherCourseDetail from './pages/teacher/CourseDetail';
import EditCourse from './pages/teacher/EditCourse';
import Lessons from './pages/teacher/Lessons';
import EditLesson from './pages/teacher/EditLesson';
import TeacherStudents from './pages/teacher/Students';
import TeacherSchedule from './pages/teacher/Schedule';
import ManageSchedule from './pages/teacher/ManageSchedule';
import TeacherReports from './pages/teacher/Reports';
import TeacherEarnings from './pages/teacher/Earnings';
import TeacherQuestions from './pages/teacher/Questions';
import TeacherMessages from './pages/teacher/Messages';
import TeachersList from './pages/shared/TeachersList';
import PublicTeacherProfile from './pages/shared/PublicTeacherProfile';
import TeacherStatistics from './pages/teacher/Statistics';
import TeacherSubjects from './pages/teacher/Subjects';
import StudentDashboard from './pages/student/Dashboard';
import StudentCourses from './pages/student/Courses';
import ExploreCourses from './pages/student/ExploreCourses';
import StudentLessons from './pages/student/Lessons';
import StudentSubscriptions from './pages/student/Subscriptions';
import StudentQuestions from './pages/student/Questions';
import PaymentConfirmation from './pages/student/PaymentConfirmation';
import ActivateCode from './pages/student/ActivateCode';
import HelpCenter from './pages/help/HelpCenter';
import PrivacyPolicy from './pages/help/PrivacyPolicy';
import TermsOfService from './pages/help/TermsOfService';
import Contact from './pages/help/Contact';
import About from './pages/shared/About';
import StudentSchedule from './pages/student/Schedule';
import StudentMessages from './pages/student/Messages';
import StudentProfile from './pages/student/StudentProfile';
import QuizPage from './pages/student/QuizPage';
import PaymentRequests from './pages/student/PaymentRequests';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import SystemSettings from './pages/admin/SystemSettings';
import CourseDetail from './pages/student/CourseDetail';
import LessonDetail from './pages/student/LessonDetail';
import FileViewer from './pages/student/FileViewer';
import MyProfile from './pages/shared/MyProfile';
import Settings from './pages/shared/Settings';
import NotFound from './pages/shared/NotFound';

import './index.css';



// 🔧 Smart retry configuration for React Query
const shouldRetryQuery = (failureCount, error) => {
  // Don't retry cancelled requests
  if (error?.code === 'ERR_CANCELED') return false;
  
  // Retry network errors and 5xx errors (max 3 times)
  if (!error?.response) return failureCount < 3;
  
  const status = error.response.status;
  
  // Retry on: 408 (Timeout), 500 (Server Error), 502 (Bad Gateway), 503 (Service Unavailable), 429 (Too Many Requests)
  const retryableStatuses = [408, 500, 502, 503, 429];
  return retryableStatuses.includes(status) && failureCount < 3;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ✅ Custom retry function instead of simple number
      retry: shouldRetryQuery,
      // ✅ Disable automatic refetch - user should manually retry
      refetchOnWindowFocus: false,
      // ✅ Increase stale time to reduce unnecessary requests
      staleTime: 60000, // 1 minute
    },
  },
});

function App() {
  // Set QueryClient for AuthContext to use for cache clearing on logout
  React.useEffect(() => {
    setQueryClient(queryClient);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AppContent />
      </ToastProvider>
    </QueryClientProvider>
  );
}

function AppContent() {
  const { toast } = useToast();
  
  // Set toast function for axios error handling
  React.useEffect(() => {
    setToastFunction(toast);
  }, [toast]);
  
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        {/* ✅ AuthProvider INSIDE Router so it can use useNavigate */}
        <AuthProvider>
          {/* ✅ SettingsProvider INSIDE AuthProvider so it can use useAuth */}
          <SettingsProvider>
            {/* ✅ NotificationProvider can access AuthContext now */}
            <NotificationProvider>
              <div className="min-h-screen bg-secondary-50">
                <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email/:token" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                
                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Home />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Dashboard />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute roles={['admin']}>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<AdminDashboard />} />
                          <Route path="/teacher-approval" element={<TeacherApproval />} />
                          <Route path="/users" element={<UsersManagement />} />
                          <Route path="/content" element={<ContentManagement />} />
                          <Route path="/subscriptions" element={<SubscriptionsManagement />} />
                          <Route path="/subscription-management" element={<SubscriptionManagement />} />
                          <Route path="/settings" element={<SystemSettings />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/teacher/*"
                  element={
                    <ProtectedRoute roles={['teacher', 'admin']}>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<TeacherDashboard />} />
                          <Route path="/courses" element={<TeacherCourses />} />
                          <Route path="/courses/:courseId" element={<TeacherCourseDetail />} />
                          <Route path="/courses/:courseId/edit" element={<EditCourse />} />
                          <Route path="/lessons" element={<Lessons />} />
                          <Route path="/lessons/:lessonId/edit" element={<EditLesson />} />
                          <Route path="/create-course" element={<CreateCourse />} />
                          <Route path="/create-lesson" element={<CreateLesson />} />
                          <Route path="/quizzes/create" element={<CreateQuiz />} />
                          <Route path="/quizzes" element={<QuizzesManagement />} />
                          <Route path="/quizzes/:quizId/edit" element={<EditQuiz />} />
                          <Route path="/quizzes/:quizId/results" element={<QuizResults />} />
                          <Route path="/students" element={<TeacherStudents />} />
                          <Route path="/schedule" element={<TeacherSchedule />} />
                          <Route path="/manage-schedule" element={<ManageSchedule />} />
                          <Route path="/reports" element={<TeacherReports />} />
                          <Route path="/revenue" element={<TeacherEarnings />} />
                          <Route path="/questions" element={<TeacherQuestions />} />
                          <Route path="/messages" element={<TeacherMessages />} />
                          <Route path="/analytics" element={<TeacherStatistics />} />
                          <Route path="/subjects" element={<TeacherSubjects />} />
                          <Route path="/profile" element={<MyProfile />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/student/*"
                  element={
                    <ProtectedRoute roles={['student']}>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<StudentDashboard />} />
                          <Route path="/courses" element={<StudentCourses />} />
                          <Route path="/lessons" element={<StudentLessons />} />
                          <Route path="/browse" element={<ExploreCourses />} />
                          <Route path="/subscriptions" element={<StudentSubscriptions />} />
                          <Route path="/questions" element={<StudentQuestions />} />
                          <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
                          <Route path="/payment-requests" element={<PaymentRequests />} />
                          <Route path="/activate-code" element={<ActivateCode />} />
                          <Route path="/schedule" element={<StudentSchedule />} />
                          <Route path="/messages" element={<StudentMessages />} />
                          <Route path="/quiz/:quizId" element={<QuizPage />} />
                        </Routes>
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/student/profile/:studentId"
                  element={
                    <ProtectedRoute roles={['teacher', 'admin']}>
                      <Layout>
                        <StudentProfile />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/teachers"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <TeachersList />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/teachers/:teacherId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <PublicTeacherProfile />
                      </Layout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/courses/:courseId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <CourseDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/lessons/:lessonId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <LessonDetail />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/student/file/:lessonId"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <FileViewer />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <MyProfile />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  }
                />
                
                {/* About Page */}
                <Route
                  path="/about"
                  element={
                    <Layout>
                      <About />
                    </Layout>
                  }
                />
                
                {/* Help & Support Pages */}
                <Route
                  path="/help"
                  element={
                    <Layout>
                      <HelpCenter />
                    </Layout>
                  }
                />
                
                <Route
                  path="/privacy"
                  element={
                    <Layout>
                      <PrivacyPolicy />
                    </Layout>
                  }
                />
                
                <Route
                  path="/terms"
                  element={
                    <Layout>
                      <TermsOfService />
                    </Layout>
                  }
                />
                
                <Route
                  path="/contact"
                  element={
                    <Layout>
                      <Contact />
                    </Layout>
                  }
                />
                
                {/* 404 */}
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
              </div>
            </NotificationProvider>
          </SettingsProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
